<script setup lang="ts">
/**
 * Footer V2 — Compliance buttons (Desktop)
 *
 * Itens (mesma origem do footer legado em components/footer/Emails.vue):
 *   - Ouvidoria          → brandContacts.customer_service.link
 *   - Denúncias          → brandContacts.complaints.link
 *   - Suporte ao Jogador → brandContacts.support.link
 *   - E-mail             → brandContacts.support.email (fallback: appearance.footer_emails.SUPPORT_EMAIL)
 * Textos via i18n: footer.customer_service_office / footer.complaints /
 * footer.support_player / footer.support.
 *
 * Cada item só é renderizado quando o link correspondente existir;
 * o divisor (|) só aparece entre dois itens visíveis.
 */
const { t } = useTexts();
const { brandContacts } = useFeatureFlagsNew();
const { supportEmail } = useSupportEmail();

const customerServiceLink = computed<string>(
  () => brandContacts.value?.customer_service?.link || '',
);

const complaintsLink = computed<string>(
  () => brandContacts.value?.complaints?.link || '',
);

const supportLink = computed<string>(
  () => brandContacts.value?.support?.link || '',
);

type Item =
  | { type: 'link'; label: string; href: string }
  | { type: 'email'; prefix: string; email: string };

const items = computed<Item[]>(() => {
  const out: Item[] = [];

  if (customerServiceLink.value)
    out.push({
      type: 'link',
      label: t('footer.customer_service_office'),
      href: customerServiceLink.value,
    });
  if (complaintsLink.value)
    out.push({
      type: 'link',
      label: t('footer.complaints'),
      href: complaintsLink.value,
    });
  if (supportLink.value)
    out.push({
      type: 'link',
      label: t('footer.support_player'),
      href: supportLink.value,
    });
  if (supportEmail.value)
    out.push({
      type: 'email',
      prefix: `${t('footer.support')} via e-mail`,
      email: supportEmail.value,
    });

  return out;
});
</script>

<template>
  <div
    v-if="items.length"
    class="footer-v2-compliance-buttons hidden lg:flex"
  >
    <template
      v-for="(item, idx) in items"
      :key="idx"
    >
      <span
        v-if="idx > 0"
        aria-hidden="true"
        class="footer-v2-compliance-buttons__divider"
        >|</span
      >

      <a
        v-if="item.type === 'link'"
        class="footer-v2-compliance-buttons__link"
        :href="item.href"
        rel="noopener noreferrer"
        target="_blank"
      >
        {{ item.label }}
      </a>

      <span
        v-else
        class="footer-v2-compliance-buttons__support-email"
      >
        {{ item.prefix }}
        <a
          class="footer-v2-compliance-buttons__email-link lowercase"
          :href="`mailto:${item.email}`"
        >
          {{ item.email }}
        </a>
      </span>
    </template>
  </div>
</template>

<style scoped lang="scss">
.footer-v2-compliance-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;

  /* Mobile: wrap + quebra */
  @media (max-width: 1023px) {
    flex-wrap: wrap;
    gap: 10px;
  }

  &__link {
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 21px;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  &__divider {
    color: rgba(255, 255, 255, 0.1);
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 21px;

    @media (max-width: 1023px) {
      display: none;
    }
  }

  &__support-email {
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 21px;
    white-space: nowrap;
  }

  &__email-link {
    color: rgba(255, 255, 255, 0.5);
    text-decoration: none;

    &:hover {
      color: rgba(255, 255, 255, 0.7);
    }
  }
}
</style>
